import React, { useEffect, useState } from 'react';
import { Users, MapPin, CalendarDays, BadgeCheck, Star, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { tradeLabel } from '@/data/constants';
import VerificationBadges from '@/components/VerificationBadges';
import StarRating from '@/components/testimonials/StarRating';
import ContractorRatingStep from '@/components/ContractorRatingStep';
import { ContractorRating, fetchRatingsForListings, formatAvg } from '@/lib/contractorRatings';

interface Resp {
  id: string;
  listing_id: string;
  buyer_id: string;
  created_at: string;
  listing_title: string;
  company?: string;
  trades?: string[];
  city?: string;
  state?: string;
  licensed?: boolean;
  insured?: boolean;
  verified?: boolean;
  /** true when the homeowner recorded this contractor as hired for the project */
  hired?: boolean;
  /** listing closed as work_completed (rating allowed for the hired contractor) */
  completed?: boolean;
  avg_rating?: number | null;
  rating_count?: number;
  jobs_won?: number;
}

/**
 * Homeowner-only "Responses" view: which contractors have unlocked each project.
 * Intentionally shows NO lead price, no balance and no contractor billing data —
 * only who is interested so the homeowner can expect their call.
 * Phase 1.1: shows each contractor's average rating + "GigGone Hired" badge,
 * the homeowner's own rating of a hired contractor, and a rate-later button.
 */
const HomeownerResponses: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { t, lang } = useI18n();
  const es = lang === 'es';
  const { profile } = useAuth();
  const [rows, setRows] = useState<Resp[]>([]);
  const [ratings, setRatings] = useState<Map<string, ContractorRating>>(new Map());
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<Resp | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      if (!profile) return;
      setLoading(true);

      const { data: listings } = await supabase
        .from('mkt_listings')
        .select('id,title,hired_buyer_id,closing_outcome')
        .eq('owner_id', profile.id);

      const ids = (listings || []).map((l: { id: string }) => l.id);
      if (!ids.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const { data: purchases } = await supabase
        .from('mkt_lead_purchases')
        .select('id,listing_id,buyer_id,created_at')
        .in('listing_id', ids)
        .order('created_at', { ascending: false });

      // p_profiles_read only returns contractors linked to THIS homeowner by a purchase.
      const buyerIds = [...new Set((purchases || []).map((p: any) => p.buyer_id).filter(Boolean))];
      const [{ data: buyers }, rmap] = await Promise.all([
        buyerIds.length
          ? supabase
              .from('mkt_profiles')
              .select('id,company_name,first_name,last_name,trades,city,state,license_verified,insurance_verified,background_verified,avg_rating,rating_count,jobs_won')
              .in('id', buyerIds)
          : Promise.resolve({ data: [] as any[] }),
        fetchRatingsForListings(ids),
      ]);

      const map = new Map((buyers || []).map((b: any) => [b.id, b]));
      const lmap = new Map((listings || []).map((l: any) => [l.id, l]));

      setRatings(rmap);
      setRows(
        (purchases || []).map((p: any) => {
          const b = map.get(p.buyer_id) || {};
          const l: any = lmap.get(p.listing_id) || {};
          return {
            id: p.id,
            listing_id: p.listing_id,
            buyer_id: p.buyer_id,
            created_at: p.created_at,
            listing_title: l.title || '',
            company: b.company_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || undefined,
            trades: b.trades || [],
            city: b.city,
            state: b.state,
            licensed: !!b.license_verified,
            insured: !!b.insurance_verified,
            verified: !!b.background_verified,
            hired: !!p.buyer_id && l.hired_buyer_id === p.buyer_id,
            completed: l.closing_outcome === 'work_completed',
            avg_rating: b.avg_rating == null ? null : Number(b.avg_rating),
            rating_count: Number(b.rating_count || 0),
            jobs_won: Number(b.jobs_won || 0),
          };
        })
      );
      setLoading(false);
    };
    load();
  }, [profile?.id, refreshKey, tick]);

  return (
    <section className="mt-12">
      <h3 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
        <Users className="h-5 w-5 text-orange-500" />
        {t('dash.responsesTitle')}
      </h3>
      <p className="mt-1 text-sm text-slate-500">{t('dash.responsesSub')}</p>

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          {t('dash.noResponses')}
        </p>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {rows.map((r) => {
            const mine = r.hired ? ratings.get(r.listing_id) : undefined;
            return (
              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{r.listing_title}</p>
                <h4 className="mt-1 text-base font-extrabold text-slate-900">{r.company || t('dash.contractorInterested')}</h4>

                {(r.rating_count || 0) > 0 && (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                    <StarRating value={Math.round(r.avg_rating || 0)} size="sm" />
                    <span className="font-bold text-slate-900">{formatAvg(r.avg_rating)}</span>
                    <span className="text-xs text-slate-400">
                      ({r.rating_count} {es ? (r.rating_count === 1 ? 'calificación' : 'calificaciones') : r.rating_count === 1 ? 'rating' : 'ratings'})
                    </span>
                  </p>
                )}

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(r.trades || []).slice(0, 3).map((tr) => (
                    <span key={tr} className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold uppercase text-white">
                      {tradeLabel(tr, lang)}
                    </span>
                  ))}
                </div>

                <div className="mt-3 grid gap-1.5 text-sm text-slate-600">
                  {(r.city || r.state) && (
                    <p className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {[r.city, r.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-slate-400" />
                    {t('dash.respondedOn')} {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {r.hired && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-bold text-white">
                      <BadgeCheck className="h-3.5 w-3.5" /> {es ? 'Contratado' : 'Hired'}
                    </span>
                  )}
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                      <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.verified')}
                    </span>
                  )}
                  {r.licensed && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">
                      <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.licensed')}
                    </span>
                  )}
                  {r.insured && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                      <BadgeCheck className="h-3.5 w-3.5" /> {t('verify.insured')}
                    </span>
                  )}
                  <VerificationBadges jobsWon={r.jobs_won} />
                </div>

                {r.hired && r.completed && (
                  mine ? (
                    <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <p className="flex items-center gap-2 font-bold">
                        {es ? 'Tu calificación:' : 'Your rating:'} <StarRating value={mine.rating} size="sm" />
                        {mine.would_hire_again != null && (
                          <span className="font-semibold">
                            · {mine.would_hire_again ? (es ? 'Lo contrataría de nuevo' : 'Would hire again') : es ? 'No lo contrataría de nuevo' : 'Would not hire again'}
                          </span>
                        )}
                      </p>
                      {mine.comment && <p className="mt-1 line-clamp-3 text-amber-800">“{mine.comment}”</p>}
                    </div>
                  ) : (
                    <button
                      onClick={() => setRating(r)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-white hover:bg-amber-600"
                    >
                      <Star className="h-4 w-4" /> {es ? 'Calificar al contratista' : 'Rate this contractor'}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {rating && profile?.id && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true">
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900">{es ? 'Califica al contratista' : 'Rate your contractor'}</h2>
                <p className="mt-0.5 line-clamp-1 text-sm text-slate-500">{rating.listing_title}</p>
              </div>
              <button onClick={() => setRating(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label={es ? 'Cerrar' : 'Close'}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <ContractorRatingStep
              es={es}
              listingId={rating.listing_id}
              homeownerId={profile.id}
              contractorId={rating.buyer_id}
              contractorName={rating.company || (es ? 'el contratista' : 'the contractor')}
              onDone={(rated) => {
                setRating(null);
                if (rated) setTick((n) => n + 1);
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HomeownerResponses;
